import { motion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import {
  Shield,
  Lock,
  FileText,
  AlertCircle,
  CheckCircle,
} from "lucide-react";

export default function PrivacyPage() {
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div className="pt-24 pb-16">
      <section className="py-16">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto"
          >
            <Badge variant="info" className="mb-4">
              Legal
            </Badge>
            <h1 className="font-display text-4xl md:text-5xl font-bold mb-4">
              Privacy Policy
            </h1>
            <p className="text-muted-foreground mb-8">
              Last updated: January 13, 2026
              <br />
              Echopeak Labs, Ontario, Canada
            </p>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-12">
              <div className="flex items-center gap-2 text-sm">
                <Shield className="h-5 w-5 text-primary" />
                <span>Privacy Compliant</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Lock className="h-5 w-5 text-primary" />
                <span>Stripe PCI Level 1</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Lock className="h-5 w-5 text-primary" />
                <span>256-bit Encryption</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <CheckCircle className="h-5 w-5 text-primary" />
                <span>Client Ownership</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <CheckCircle className="h-5 w-5 text-primary" />
                <span>ISO 27001 Inspired</span>
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-6 mb-12">
              <h2 className="font-display text-xl font-bold mb-4">
                Quick Navigation
              </h2>
              <div className="grid md:grid-cols-2 gap-2 text-sm">
                <button
                  onClick={() => scrollToSection("introduction")}
                  className="text-left hover:text-primary transition-colors"
                >
                  1. Introduction
                </button>
                <button
                  onClick={() => scrollToSection("definitions")}
                  className="text-left hover:text-primary transition-colors"
                >
                  2. Definitions
                </button>
                <button
                  onClick={() => scrollToSection("collection")}
                  className="text-left hover:text-primary transition-colors"
                >
                  3. Information We Collect
                </button>
                <button
                  onClick={() => scrollToSection("ownership")}
                  className="text-left hover:text-primary transition-colors"
                >
                  4. Data Ownership
                </button>
                <button
                  onClick={() => scrollToSection("protection")}
                  className="text-left hover:text-primary transition-colors"
                >
                  5. How We Protect Your Data
                </button>
                <button
                  onClick={() => scrollToSection("storage")}
                  className="text-left hover:text-primary transition-colors"
                >
                  6. Data Storage & Hosting
                </button>
                <button
                  onClick={() => scrollToSection("access")}
                  className="text-left hover:text-primary transition-colors"
                >
                  7. Access Controls
                </button>
                <button
                  onClick={() => scrollToSection("usage")}
                  className="text-left hover:text-primary transition-colors"
                >
                  8. How We Use Your Information
                </button>
                <button
                  onClick={() => scrollToSection("sharing")}
                  className="text-left hover:text-primary transition-colors"
                >
                  9. Information Sharing
                </button>
                <button
                  onClick={() => scrollToSection("payment")}
                  className="text-left hover:text-primary transition-colors"
                >
                  9A. Payment & PCI Compliance
                </button>
                <button
                  onClick={() => scrollToSection("retention")}
                  className="text-left hover:text-primary transition-colors"
                >
                  10. Data Retention
                </button>
                <button
                  onClick={() => scrollToSection("confidentiality")}
                  className="text-left hover:text-primary transition-colors"
                >
                  11. Confidentiality Commitments
                </button>
                <button
                  onClick={() => scrollToSection("breach")}
                  className="text-left hover:text-primary transition-colors"
                >
                  12. Data Breach Notification
                </button>
                <button
                  onClick={() => scrollToSection("rights")}
                  className="text-left hover:text-primary transition-colors"
                >
                  13. Your Rights
                </button>
                <button
                  onClick={() => scrollToSection("compliance")}
                  className="text-left hover:text-primary transition-colors"
                >
                  14. Compliance Framework
                </button>
                <button
                  onClick={() => scrollToSection("cookies")}
                  className="text-left hover:text-primary transition-colors"
                >
                  15. Cookies
                </button>
                <button
                  onClick={() => scrollToSection("contact")}
                  className="text-left hover:text-primary transition-colors"
                >
                  16. Contact Us
                </button>
              </div>
            </div>

            <div className="prose prose-invert prose-lg max-w-none">
              <h2
                id="introduction"
                className="font-display text-2xl font-bold mt-8 mb-4"
              >
                1. Introduction
              </h2>
              <p className="text-muted-foreground mb-6">
                Echopeak Labs ("we," "our," or "us") is committed to protecting
                your privacy and safeguarding your intellectual property,
                business ideas, source code, and all project materials. This
                Privacy Policy explains how we collect, use, disclose, and
                safeguard your information when you visit our website or use our
                services.
              </p>

              <h3 className="font-display text-xl font-semibold mt-6 mb-3">
                Multi-Jurisdictional Privacy Compliance
              </h3>
              <p className="text-muted-foreground mb-4">
                As a Canadian company based in Ontario, we comply with
                applicable privacy laws based on your location:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground mb-6 space-y-2">
                <li>
                  <strong>Canadian Clients:</strong> We comply with Canada's
                  Personal Information Protection and Electronic Documents Act
                  (PIPEDA) and Ontario privacy legislation
                </li>
                <li>
                  <strong>US Clients:</strong> We adhere to applicable US
                  privacy laws, including state-specific regulations such as the
                  California Consumer Privacy Act (CCPA) where applicable
                </li>
                <li>
                  <strong>All Clients:</strong> We follow industry best
                  practices and international privacy standards to protect your
                  information regardless of location
                </li>
              </ul>

              <p className="text-muted-foreground mb-6">
                This policy applies to all current and prospective clients,
                project-related data, intellectual property created during
                client engagements, and all confidential information shared with
                Echopeak Labs.
              </p>

              <h2
                id="definitions"
                className="font-display text-2xl font-bold mt-12 mb-4"
              >
                2. Definitions of Client Property
              </h2>
              <p className="text-muted-foreground mb-4">
                We recognize and protect the following types of client property.
                All intellectual property created during engagements belongs to
                the client and resides in client-owned infrastructure:
              </p>

              <h3 className="font-display text-xl font-semibold mt-6 mb-3">
                Intellectual Property (IP)
              </h3>
              <p className="text-muted-foreground mb-4">
                All IP is created directly in client-owned repositories and
                infrastructure:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground mb-6 space-y-2">
                <li>
                  Source code and compiled applications (stored in client
                  repositories)
                </li>
                <li>
                  Algorithms and technical specifications (owned by client from
                  creation)
                </li>
                <li>
                  Database schemas and architectures (deployed in client
                  infrastructure)
                </li>
                <li>User interfaces and design elements (owned by client)</li>
                <li>Documentation and technical writings (owned by client)</li>
              </ul>

              <h3 className="font-display text-xl font-semibold mt-6 mb-3">
                Business Ideas and Concepts
              </h3>
              <ul className="list-disc pl-6 text-muted-foreground mb-6 space-y-2">
                <li>Business models and strategies</li>
                <li>Market analysis and research</li>
                <li>Product concepts and features</li>
                <li>Competitive advantages and unique selling propositions</li>
                <li>Customer acquisition strategies</li>
              </ul>

              <h3 className="font-display text-xl font-semibold mt-6 mb-3">
                Project Materials
              </h3>
              <ul className="list-disc pl-6 text-muted-foreground mb-6 space-y-2">
                <li>Design mockups and wireframes</li>
                <li>Project requirements and specifications</li>
                <li>Test cases and quality assurance materials</li>
                <li>Deployment configurations</li>
                <li>Third-party integrations and API documentation</li>
              </ul>

              <p className="text-muted-foreground mb-6">
                <strong>Important:</strong> Echopeak Labs does not host or store
                client application code or data. All work is performed directly
                in client-owned repositories (GitHub, GitLab, etc.) and
                infrastructure (AWS, Azure, GCP, etc.).
              </p>

              <h2
                id="collection"
                className="font-display text-2xl font-bold mt-12 mb-4"
              >
                3. Information We Collect
              </h2>
              <p className="text-muted-foreground mb-4">
                We collect information you provide directly to us, such as:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground mb-6 space-y-2">
                <li>Name and contact information (email, phone number)</li>
                <li>Company information and job title</li>
                <li>Project details and requirements</li>
                <li>Payment and billing information</li>
                <li>Communications with our team</li>
                <li>
                  Business strategies and ideas you share with us during
                  consultation
                </li>
                <li>
                  Project documentation and designs created during collaboration
                </li>
              </ul>
              <p className="text-muted-foreground mb-6">
                We practice data minimization and collect only the information
                necessary for project execution and service delivery. All source
                code, application data, and production systems remain in
                client-owned repositories and infrastructure at all times.
              </p>

              <h2
                id="ownership"
                className="font-display text-2xl font-bold mt-12 mb-4"
              >
                4. Data Ownership and Client Infrastructure
              </h2>
              <p className="text-muted-foreground mb-4">
                <strong>
                  You own 100% of your intellectual property and infrastructure.
                </strong>{" "}
                All work created by Echopeak Labs for clients is considered
                "work for hire," which means:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground mb-6 space-y-2">
                <li>
                  You own all intellectual property from the moment of creation
                </li>
                <li>
                  Echopeak Labs has no ownership claim to your deliverables
                </li>
                <li>All rights, title, and interest belong to you</li>
                <li>
                  We provide warranty that work is original and non-infringing
                </li>
                <li>
                  All work is performed directly in your repositories and
                  infrastructure
                </li>
              </ul>
              <p className="text-muted-foreground mb-4">
                <strong>Client Infrastructure Requirements:</strong>
              </p>
              <ul className="list-disc pl-6 text-muted-foreground mb-6 space-y-2">
                <li>
                  Clients must provide their own code repository access (GitHub,
                  GitLab, Bitbucket, etc.)
                </li>
                <li>
                  Clients must provide their own cloud infrastructure account
                  (AWS, Azure, GCP, etc.)
                </li>
                <li>
                  All development work occurs directly in client-owned
                  environments
                </li>
                <li>
                  Echopeak Labs does not host, own, or maintain any client
                  applications or data
                </li>
                <li>
                  Clients are responsible for all infrastructure costs and
                  operational expenses
                </li>
              </ul>
              <p className="text-muted-foreground mb-6">
                We may include completed projects in our portfolio only with
                your explicit written permission and only for publicly
                available, non-confidential aspects of the work.
              </p>

              <h2
                id="protection"
                className="font-display text-2xl font-bold mt-12 mb-4"
              >
                5. How We Protect Your Data
              </h2>
              <p className="text-muted-foreground mb-4">
                We implement comprehensive security measures to protect your
                information:
              </p>

              <h3 className="font-display text-xl font-semibold mt-6 mb-3">
                AWS Security Infrastructure
              </h3>
              <ul className="list-disc pl-6 text-muted-foreground mb-6 space-y-2">
                <li>
                  <strong>Encryption:</strong> 256-bit encryption for all data
                  at rest and in transit using TLS 1.3
                </li>
                <li>
                  <strong>Authentication:</strong> Multi-factor authentication
                  (MFA) required for all system access
                </li>
                <li>
                  <strong>Monitoring:</strong> 24/7 security monitoring and
                  threat detection
                </li>
                <li>
                  <strong>Updates:</strong> Regular security updates and patches
                </li>
                <li>
                  <strong>Key Management:</strong> AWS Key Management Service
                  (KMS) for secure encryption keys
                </li>
              </ul>

              <h3 className="font-display text-xl font-semibold mt-6 mb-3">
                Network Security
              </h3>
              <ul className="list-disc pl-6 text-muted-foreground mb-6 space-y-2">
                <li>
                  <strong>VPC Configuration:</strong> Private subnets for
                  sensitive operations
                </li>
                <li>
                  <strong>Security Groups:</strong> Restrictive network access
                  controls
                </li>
                <li>
                  <strong>DNS Security:</strong> Route 53 with DNSSEC enabled
                </li>
                <li>
                  <strong>API Security:</strong> JWT tokens with expiration and
                  secure session management
                </li>
              </ul>

              <h2
                id="storage"
                className="font-display text-2xl font-bold mt-12 mb-4"
              >
                6. Data Storage & Hosting
              </h2>

              <h3 className="font-display text-xl font-semibold mt-6 mb-3">
                Our Platform Infrastructure
              </h3>
              <p className="text-muted-foreground mb-4">
                Echopeak Labs operates a client portal and project management
                platform hosted on Amazon Web Services (AWS). This platform is
                used exclusively for:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground mb-6 space-y-2">
                <li>Client account management and authentication</li>
                <li>Project proposals, contracts, and documentation</li>
                <li>Communication between clients and our team</li>
                <li>Billing and payment processing integration</li>
                <li>Project tracking and status updates</li>
              </ul>

              <p className="text-muted-foreground mb-4">
                Our platform uses the following AWS infrastructure:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground mb-6 space-y-2">
                <li>
                  <strong>Compute:</strong> AWS Lambda with secure IAM roles
                </li>
                <li>
                  <strong>Database:</strong> DynamoDB with encryption at rest
                  and on-demand encryption
                </li>
                <li>
                  <strong>File Storage:</strong> S3 with server-side encryption
                  and versioning
                </li>
                <li>
                  <strong>Authentication:</strong> AWS Cognito with secure
                  password policies and MFA
                </li>
                <li>
                  <strong>API Gateway:</strong> Request validation and rate
                  limiting
                </li>
              </ul>

              <h3 className="font-display text-xl font-semibold mt-6 mb-3">
                Client Application Infrastructure
              </h3>
              <p className="text-muted-foreground mb-6">
                <strong>Important:</strong> Client applications, source code,
                and production data are NOT hosted by Echopeak Labs. All client
                applications reside in client-owned infrastructure (AWS, Azure,
                GCP, etc.) and repositories (GitHub, GitLab, Bitbucket, etc.).
                Clients maintain full control and ownership of their
                applications and data at all times.
              </p>

              <p className="text-muted-foreground mb-6">
                <strong>Data Residency:</strong> Our platform data is stored in
                compliance with Canadian data residency requirements and
                applicable laws. AWS data centers are selected to meet legal and
                regulatory obligations.
              </p>

              <div className="bg-card border border-border rounded-lg p-4 mb-6">
                <div className="flex items-start gap-3">
                  <FileText className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-semibold mb-2">
                      What We Store on Our Platform
                    </p>
                    <ul className="text-sm text-muted-foreground space-y-1">
                      <li>• Client account information and contact details</li>
                      <li>• Project contracts and proposals</li>
                      <li>
                        • Communication history between clients and our team
                      </li>
                      <li>• Billing and invoice records</li>
                      <li>• Project documentation and requirements</li>
                    </ul>
                    <p className="text-sm text-muted-foreground mt-3">
                      <strong>What We Do NOT Store:</strong> Client application
                      source code, production data, user data from client
                      applications, or any client application infrastructure.
                    </p>
                  </div>
                </div>
              </div>

              <h2
                id="access"
                className="font-display text-2xl font-bold mt-12 mb-4"
              >
                7. Access Controls
              </h2>

              <h3 className="font-display text-xl font-semibold mt-6 mb-3">
                Our Platform Access Controls
              </h3>
              <p className="text-muted-foreground mb-4">
                We implement strict role-based access control (RBAC) for our
                client portal and project management platform:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground mb-6 space-y-2">
                <li>
                  <strong>Project-Specific Access:</strong> Team members only
                  access data necessary for their assigned projects
                </li>
                <li>
                  <strong>Client Access:</strong> Secure client portal with
                  separate authentication pools
                </li>
                <li>
                  <strong>Admin Access:</strong> Enhanced security with
                  mandatory MFA for administrative functions
                </li>
                <li>
                  <strong>Regular Reviews:</strong> Monthly access validation
                  and permission audits
                </li>
                <li>
                  <strong>Access Logging:</strong> All access to platform data
                  is logged and monitored
                </li>
              </ul>

              <h3 className="font-display text-xl font-semibold mt-6 mb-3">
                Client Infrastructure Access
              </h3>
              <p className="text-muted-foreground mb-4">
                For development work in client-owned infrastructure:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground mb-6 space-y-2">
                <li>
                  <strong>Controlled Access:</strong> Clients grant and revoke
                  access to their repositories and cloud accounts
                </li>
                <li>
                  <strong>Principle of Least Privilege:</strong> Team members
                  receive only the permissions necessary for their work
                </li>
                <li>
                  <strong>Audit Trail:</strong> All actions in client
                  infrastructure are logged by client's systems
                </li>
                <li>
                  <strong>Time-Limited Access:</strong> Access is granted for
                  project duration and revoked upon completion
                </li>
                <li>
                  <strong>Client Control:</strong> Clients maintain full
                  administrative control over their infrastructure at all times
                </li>
              </ul>

              <h2
                id="usage"
                className="font-display text-2xl font-bold mt-12 mb-4"
              >
                8. How We Use Your Information
              </h2>
              <p className="text-muted-foreground mb-4">
                We use the information we collect to:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground mb-6 space-y-2">
                <li>Provide and maintain our services</li>
                <li>Develop and deliver your software projects</li>
                <li>Process transactions and send related information</li>
                <li>Respond to your inquiries and provide customer support</li>
                <li>Send project updates and important notifications</li>
                <li>
                  Analyze and improve our services (using anonymized data)
                </li>
                <li>Comply with legal obligations</li>
              </ul>
              <p className="text-muted-foreground mb-6">
                We require explicit consent for data collection and provide
                granular consent options. You may withdraw consent at any time.
              </p>

              <h2
                id="sharing"
                className="font-display text-2xl font-bold mt-12 mb-4"
              >
                9. Information Sharing
              </h2>
              <p className="text-muted-foreground mb-6">
                <strong>
                  We do not sell your personal information or intellectual
                  property.
                </strong>{" "}
                We maintain strict confidentiality and may share platform data
                only in the following limited circumstances:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground mb-6 space-y-2">
                <li>
                  <strong>Service Providers:</strong> AWS (platform hosting),
                  Stripe (payment processing), and email service providers who
                  are bound by contractual obligations to keep information
                  confidential. These providers only have access to data on our
                  platform (account info, contracts, communications), not client
                  application data
                </li>
                <li>
                  <strong>Legal Requirements:</strong> When required by law or
                  to protect our legal rights
                </li>
                <li>
                  <strong>With Your Consent:</strong> When you explicitly
                  authorize us to share information
                </li>
              </ul>
              <p className="text-muted-foreground mb-6">
                All third-party service providers are carefully vetted for
                security practices and compliance. AWS maintains SOC 2 Type II
                certification and undergoes regular independent security audits.
              </p>
              <p className="text-muted-foreground mb-6">
                <strong>Client Application Data:</strong> Since client
                applications and data reside in client-owned infrastructure,
                clients control all data sharing for their applications.
                Echopeak Labs does not have access to share client application
                data with third parties.
              </p>

              <h2
                id="payment"
                className="font-display text-2xl font-bold mt-12 mb-4"
              >
                9A. Payment Processing & PCI Compliance
              </h2>
              <p className="text-muted-foreground mb-6">
                We prioritize the security of your payment information and
                maintain strict PCI DSS (Payment Card Industry Data Security
                Standard) compliance through our payment processor.
              </p>

              <h3 className="font-display text-xl font-semibold mt-6 mb-3">
                Stripe Payment Processing
              </h3>
              <p className="text-muted-foreground mb-4">
                All payment transactions are processed by{" "}
                <strong>Stripe</strong>, a PCI Level 1 Service Provider—the
                highest level of certification in the payments industry. This
                means:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground mb-6 space-y-2">
                <li>
                  <strong>Secure Processing:</strong> All payment card
                  information is handled directly by Stripe using their secure
                  infrastructure
                </li>
                <li>
                  <strong>Zero Storage:</strong> We do not store, process, or
                  have access to your complete payment card details on our
                  servers
                </li>
                <li>
                  <strong>PCI Compliance:</strong> Stripe's PCI Level 1
                  certification ensures the highest standards of payment
                  security
                </li>
                <li>
                  <strong>PII Protection:</strong> Personally Identifiable
                  Information (PII) related to payments is managed exclusively
                  by Stripe in compliance with PCI DSS requirements
                </li>
                <li>
                  <strong>Encrypted Transmission:</strong> All payment data is
                  transmitted using industry-standard encryption protocols (TLS
                  1.3)
                </li>
              </ul>

              <h3 className="font-display text-xl font-semibold mt-6 mb-3">
                Your PCI Compliance
              </h3>
              <p className="text-muted-foreground mb-6">
                By using Stripe's secure integration methods (Checkout and
                Elements), your payment information never touches our servers,
                significantly reducing PCI compliance scope for both you and us.
                Stripe handles all aspects of payment security, including
                tokenization, fraud detection, and secure card storage.
              </p>

              <div className="bg-card border border-primary/20 rounded-lg p-4 mb-6">
                <div className="flex items-start gap-3">
                  <Lock className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-semibold mb-2">
                      Payment Security Guarantee
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Your payment information is protected by Stripe's
                      enterprise-grade security infrastructure, which is trusted
                      by millions of businesses worldwide. For more details,
                      visit{" "}
                      <a
                        href="https://stripe.com/docs/security"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline"
                      >
                        Stripe's Security Documentation
                      </a>
                      .
                    </p>
                  </div>
                </div>
              </div>

              <h2
                id="retention"
                className="font-display text-2xl font-bold mt-12 mb-4"
              >
                10. Data Retention
              </h2>
              <p className="text-muted-foreground mb-4">
                We retain platform data according to the following policies:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground mb-6 space-y-2">
                <li>
                  <strong>Account Information:</strong> Retained while account
                  is active and for 1 year after account closure
                </li>
                <li>
                  <strong>Contracts and Proposals:</strong> Retained for 7 years
                  as required by Canadian law
                </li>
                <li>
                  <strong>Communications:</strong> Project communications
                  retained for 1 year after project completion
                </li>
                <li>
                  <strong>Billing Records:</strong> Tax and invoice records
                  retained for 7 years as required by Canadian law
                </li>
                <li>
                  <strong>Access Logs:</strong> Security logs for our platform
                  retained for 90 days
                </li>
                <li>
                  <strong>Project Documentation:</strong> Requirements and
                  specifications retained for 1 year after project completion
                </li>
              </ul>
              <p className="text-muted-foreground mb-6">
                <strong>Client Application Data:</strong> Since all client
                applications and source code reside in client-owned
                infrastructure, clients maintain full control over retention of
                their application data, source code, and production systems.
                Echopeak Labs does not retain copies of client application code
                or data after project completion.
              </p>
              <p className="text-muted-foreground mb-6">
                After retention periods expire, we securely delete data from our
                platform using multiple overwrite passes and remove it from all
                backup systems.
              </p>

              <h2
                id="confidentiality"
                className="font-display text-2xl font-bold mt-12 mb-4"
              >
                11. Confidentiality Commitments
              </h2>
              <p className="text-muted-foreground mb-4">
                We take confidentiality seriously:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground mb-6 space-y-2">
                <li>
                  <strong>Non-Disclosure Agreements:</strong> All client
                  engagements begin with signed NDAs covering all confidential
                  information with a standard 5-year term (or longer as
                  specified)
                </li>
                <li>
                  <strong>Employee Agreements:</strong> All team members sign
                  comprehensive confidentiality agreements with strict
                  non-compete and non-solicitation clauses
                </li>
                <li>
                  <strong>Background Checks:</strong> Conducted for all team
                  members with access to client data
                </li>
                <li>
                  <strong>Security Training:</strong> Regular security awareness
                  programs for all staff
                </li>
                <li>
                  <strong>Post-Project Confidentiality:</strong> Confidentiality
                  obligations survive project completion
                </li>
              </ul>
              <p className="text-muted-foreground mb-6">
                We commit to never disclose client information to third parties,
                never use client ideas for other projects, and never discuss
                client projects without explicit permission.
              </p>

              <h2
                id="breach"
                className="font-display text-2xl font-bold mt-12 mb-4"
              >
                12. Data Breach Notification
              </h2>
              <p className="text-muted-foreground mb-4">
                In the unlikely event of a data breach, we have comprehensive
                incident response procedures:
              </p>

              <h3 className="font-display text-xl font-semibold mt-6 mb-3">
                Incident Response
              </h3>
              <ul className="list-disc pl-6 text-muted-foreground mb-6 space-y-2">
                <li>Immediate assessment upon detection</li>
                <li>Containment of affected systems</li>
                <li>Investigation of breach scope and impact</li>
                <li>Notification of appropriate parties</li>
              </ul>

              <h3 className="font-display text-xl font-semibold mt-6 mb-3">
                Notification Timeline
              </h3>
              <ul className="list-disc pl-6 text-muted-foreground mb-6 space-y-2">
                <li>
                  <strong>Critical Breaches:</strong> Within 24 hours of
                  detection
                </li>
                <li>
                  <strong>Significant Breaches:</strong> Within 48 hours of
                  detection
                </li>
                <li>
                  <strong>Minor Breaches:</strong> Within 72 hours of detection
                </li>
                <li>
                  <strong>Regulatory Notification:</strong> As required by
                  PIPEDA and applicable law
                </li>
              </ul>

              <div className="bg-card border border-yellow-500/20 rounded-lg p-4 mb-6">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-yellow-500 mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-semibold mb-2">
                      Security Incident Contact
                    </p>
                    <p className="text-sm text-muted-foreground">
                      If you suspect a security incident, please contact us
                      immediately at security@echopeaklabs.com
                    </p>
                  </div>
                </div>
              </div>

              <h2
                id="rights"
                className="font-display text-2xl font-bold mt-12 mb-4"
              >
                13. Your Rights
              </h2>
              <p className="text-muted-foreground mb-4">
                Under PIPEDA and applicable privacy laws, you have the following
                rights:
              </p>

              <h3 className="font-display text-xl font-semibold mt-6 mb-3">
                Data Access Rights
              </h3>
              <p className="text-muted-foreground mb-4">
                For data stored on our platform:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground mb-6 space-y-2">
                <li>Access your account information and profile data</li>
                <li>Review contracts, proposals, and agreements</li>
                <li>Access communication history with our team</li>
                <li>Review billing and payment records</li>
                <li>Inspect security logs related to your account</li>
              </ul>

              <h3 className="font-display text-xl font-semibold mt-6 mb-3">
                Data Portability
              </h3>
              <p className="text-muted-foreground mb-4">
                For data stored on our platform:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground mb-6 space-y-2">
                <li>Export account and project data in standard formats</li>
                <li>Download contracts and documentation</li>
                <li>Export communication history</li>
                <li>Obtain copies of all records related to your account</li>
              </ul>

              <p className="text-muted-foreground mb-6">
                <strong>Client Application Data:</strong> Since all source code
                and application data resides in your own repositories and
                infrastructure, you have complete and direct access to all your
                application code, data, and configurations at all times. You do
                not need to request access or export from Echopeak Labs for your
                application assets.
              </p>

              <h3 className="font-display text-xl font-semibold mt-6 mb-3">
                Additional Rights
              </h3>
              <ul className="list-disc pl-6 text-muted-foreground mb-6 space-y-2">
                <li>The right to rectification of inaccurate data</li>
                <li>
                  The right to erasure of your data from our active systems
                </li>
                <li>The right to restrict processing</li>
                <li>The right to object to processing</li>
                <li>The right to withdraw consent at any time</li>
              </ul>

              <h3 className="font-display text-xl font-semibold mt-6 mb-3">
                Dispute Resolution
              </h3>
              <p className="text-muted-foreground mb-6">
                If you have concerns about our privacy practices, we offer an
                internal review process, mediation options for complex issues,
                and arbitration as outlined in client agreements. Ontario
                jurisdiction applies for legal matters.
              </p>

              <h2
                id="compliance"
                className="font-display text-2xl font-bold mt-12 mb-4"
              >
                14. Compliance Framework
              </h2>
              <p className="text-muted-foreground mb-4">
                Echopeak Labs adheres to the following compliance standards
                based on applicable jurisdictions:
              </p>

              <h3 className="font-display text-xl font-semibold mt-6 mb-3">
                Canadian Legal Requirements
              </h3>
              <p className="text-muted-foreground mb-4">
                For Canadian clients and as a Canadian-based company:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground mb-6 space-y-2">
                <li>
                  <strong>PIPEDA:</strong> Personal Information Protection and
                  Electronic Documents Act compliance
                </li>
                <li>
                  <strong>Ontario Privacy Legislation:</strong> Provincial
                  privacy law compliance
                </li>
                <li>
                  <strong>Canadian Copyright Law:</strong> Full adherence to
                  intellectual property regulations
                </li>
                <li>
                  <strong>Contract Law:</strong> Enforceable agreements under
                  Canadian law
                </li>
              </ul>

              <h3 className="font-display text-xl font-semibold mt-6 mb-3">
                US Privacy Law Considerations
              </h3>
              <p className="text-muted-foreground mb-4">
                For US-based clients, we respect applicable US privacy
                regulations:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground mb-6 space-y-2">
                <li>
                  <strong>CCPA:</strong> California Consumer Privacy Act
                  compliance for California residents
                </li>
                <li>
                  <strong>State Privacy Laws:</strong> Adherence to applicable
                  state-specific privacy regulations
                </li>
                <li>
                  <strong>Cross-Border Data Transfers:</strong> Secure handling
                  of data across US-Canada border
                </li>
                <li>
                  <strong>Client Rights:</strong> Honoring privacy rights under
                  applicable US laws
                </li>
              </ul>

              <h3 className="font-display text-xl font-semibold mt-6 mb-3">
                Industry Best Practices
              </h3>
              <ul className="list-disc pl-6 text-muted-foreground mb-6 space-y-2">
                <li>
                  <strong>ISO 27001:</strong> Security practices informed by ISO
                  27001 information security standards (not formally certified)
                </li>
                <li>
                  <strong>OWASP:</strong> Web application security guidelines
                  implementation
                </li>
                <li>
                  <strong>NIST Framework:</strong> Cybersecurity best practices
                </li>
                <li>
                  <strong>Secure SDLC:</strong> Secure Software Development
                  Lifecycle practices
                </li>
              </ul>

              <h2
                id="cookies"
                className="font-display text-2xl font-bold mt-12 mb-4"
              >
                15. Cookies and Tracking
              </h2>
              <p className="text-muted-foreground mb-6">
                We use cookies and similar tracking technologies to track
                activity on our website and hold certain information. Cookies
                help us improve your experience by remembering your preferences
                and providing analytics to improve our services.
              </p>
              <p className="text-muted-foreground mb-6">
                You can instruct your browser to refuse all cookies or to
                indicate when a cookie is being sent. However, some features of
                our website may not function properly without cookies. We use
                cookies for:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground mb-6 space-y-2">
                <li>Session management and authentication</li>
                <li>User preferences and settings</li>
                <li>Analytics and performance monitoring (anonymized)</li>
                <li>Security and fraud prevention</li>
              </ul>

              <h2
                id="contact"
                className="font-display text-2xl font-bold mt-12 mb-4"
              >
                16. Contact Us
              </h2>
              <p className="text-muted-foreground mb-6">
                If you have any questions about this Privacy Policy or wish to
                exercise your privacy rights, please contact us:
              </p>

              <div className="bg-card border border-border rounded-lg p-6 mb-6">
                <h3 className="font-semibold mb-4">General Inquiries</h3>
                <p className="text-muted-foreground mb-2">
                  Email: contact@echopeaklabs.com
                </p>
                <p className="text-muted-foreground mb-4">
                  Location: Ontario, Canada
                </p>

                <h3 className="font-semibold mb-4 mt-6">
                  Privacy & Security Matters
                </h3>
                <ul className="space-y-2 text-muted-foreground">
                  <li>
                    <strong>Data Protection Officer:</strong>{" "}
                    dpo@echopeaklabs.com
                  </li>
                  <li>
                    <strong>Security Incidents:</strong>{" "}
                    security@echopeaklabs.com
                  </li>
                  <li>
                    <strong>Legal Inquiries:</strong> legal@echopeaklabs.com
                  </li>
                </ul>

                <h3 className="font-semibold mb-4 mt-6">Support</h3>
                <ul className="space-y-2 text-muted-foreground">
                  <li>
                    <strong>Client Support:</strong> support@echopeaklabs.com
                  </li>
                  <li>
                    <strong>Technical Support:</strong> tech@echopeaklabs.com
                  </li>
                </ul>
              </div>

              <div className="bg-card border border-primary/20 rounded-lg p-6 mt-8">
                <h3 className="font-display text-xl font-semibold mb-4">
                  Policy Updates
                </h3>
                <p className="text-muted-foreground mb-4">
                  This privacy policy is reviewed annually and updated as needed
                  to reflect changes in technology, security practices, legal
                  requirements, and feedback from clients and stakeholders.
                </p>
                <p className="text-muted-foreground">
                  Clients will be notified of significant policy changes at
                  least 30 days before implementation via email and through our
                  client portal.
                </p>
                <div className="mt-4 pt-4 border-t border-border">
                  <p className="text-sm text-muted-foreground">
                    <strong>Version:</strong> 2.0
                    <br />
                    <strong>Last Updated:</strong> January 13, 2026
                    <br />
                    <strong>Next Review:</strong> January 13, 2027
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
